import { AuthClient } from '@/api/auth-client'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'

export const CumulioAPI = new class CumulioClient extends AuthClient {

    async query(query: CumulioQuery): Promise<CumulioResult[]> {
        const response = await this.client.post(`${this.basePath}/service/cumulio/query`, query, {
            headers: {
                Accept: 'application/json',
            },
        })

        return response.data.map((data: string|number) => {
            const result = {}

            query.columns.forEach((column: CumulioColumn, columnIndex: number) => {
                result[column.column_id] = data[columnIndex]
            })

            return result
        })
    }

}()

export class CumulioQuery {
    id: string
    columns: CumulioColumn[] = []
    filters: Array<CumulioFilter|CumulioOrFilter> = []
    user: CumulioUser
    options = {
        pushdown: true,
    }

    constructor(id: string, user: User|CumulioUser) {
        this.id = id
        this.user = user instanceof User ? CumulioUser.fromUser(user) : user
    }

    addColumn(columnId: string, { aggregation = null, level  = null }: { aggregation?: CumulioColumnAggregation, level?: CumulioColumnLevel } = {}): void {
        this.columns.push({
            column_id: columnId,
            aggregation,
            level,
        })
    }

    addFilter(columnId: string, expression: CumulioFilterExpression, value: CumulioFilterValue = null): void {
        this.filters.push({
            column_id: columnId,
            expression,
            value,
        })
    }

    addOrFilter(filters: Array<{ columnId: string, expression: CumulioFilterExpression, value: CumulioFilterValue }>): void {
        this.filters.push(
            filters.map(filter => {
                return {
                    column_id: filter.columnId,
                    expression: filter.expression,
                    value: filter.value,
                }
            }),
        )
    }

    addUsersFilter(columnId: string, users: User[]) {
        this.addFilter(
            columnId,
            'in',
            users.map((user: User) => user.id),
        )
    }

    addBranchesFilter(columnId: string, branches: Branch[]) {
        this.addFilter(
            columnId,
            'in',
            branches.map((branch: Branch) => branch.id),
        )
    }
}

export interface CumulioColumn {
    column_id: string
    aggregation: CumulioColumnAggregation
    level: CumulioColumnLevel
}

export type CumulioColumnAggregation =
    | 'sum'
    | 'count'
    | 'distinct'
    | 'min'
    | 'max'

export type CumulioColumnLevel =
    | 'year'
    | 'quarter'
    | 'month'
    | 'week'
    | 'day'
    | 'hour'
    | 'minute'
    | 'second'
    | 'millisecond'

export interface CumulioFilter {
    column_id: string,
    expression: CumulioFilterExpression,
    value: CumulioFilterValue,
}

export type CumulioOrFilter = CumulioFilter[]

export type CumulioFilterExpression =
    | '='
    | '>'
    | '>='
    | '<'
    | '<='
    | 'is null'
    | 'is not null'
    | 'in'
    | 'not in'

export type CumulioFilterValue = string | number | string[] | number[] | null

export class CumulioUser {
    constructor(
        public id: string,
        public username: string,
        public name: string,
        public email: string,
    ) { }

    static fromUser(user: User): CumulioUser {
        return new CumulioUser(
            user.id,
            user.email,
            user.name,
            user.email,
        )
    }
}

export interface CumulioResult {
    [key: string]: string|number
}
